/**
 * Merchant-first diagnostic event timeline.
 *
 * The default view prioritises what happened and when. Correlation IDs and the
 * raw payload stay available behind disclosure for support workflows.
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import {
  CheckCircle,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  Warning as WarningIcon,
} from '@material-ui/icons';
import styles from './DiagnosticsEventPanel.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const SEVERITY_FILTERS = ['all', 'critical', 'error', 'warning', 'info'];

function humanize(value, t) {
  if (!value) return t('operator.diag.events.general');
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function severityIcon(severity) {
  if (severity === 'critical' || severity === 'error') return <ErrorIcon />;
  if (severity === 'warning') return <WarningIcon />;
  return <InfoOutlined />;
}

function EventRow({ event }) {
  const t = useLocalizedMessage();
  const [expanded, setExpanded] = useState(false);
  const formattedTime = event.occurredAt
    ? new Date(event.occurredAt).toLocaleString()
    : t('operator.diag.events.timeUnavailable');
  const repeatCount = event.repeatCount || event.dedupCount || 1;
  const hasDetails = event.source || event.jobId || event.requestId
    || event.fingerprint || repeatCount > 1 || event.payload || event.payloadTruncated;

  return (
    <li className={styles.eventItem} data-severity={event.severity}>
      <button
        type="button"
        className={styles.eventRow}
        onClick={() => hasDetails && setExpanded((value) => !value)}
        aria-expanded={hasDetails ? expanded : undefined}
        disabled={!hasDetails}
        data-testid={`event-row-${event.id}`}
      >
        <span className={styles.severityIcon} aria-label={humanize(event.severity)}>
          {severityIcon(event.severity)}
        </span>
        <span className={styles.eventBody}>
          <span className={styles.eventMessage}>{event.message}</span>
          <span className={styles.eventMeta}>
            <span className={styles.scopeChip}>{humanize(event.scope)}</span>
            <span>{formattedTime}</span>
            {repeatCount > 1 && <span>{t('operator.diag.events.repeated', { count: repeatCount })}</span>}
          </span>
        </span>
        <span className={styles.eventCode}>{event.code}</span>
        {hasDetails && (
          <span className={styles.expandIcon} aria-hidden="true">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </span>
        )}
      </button>

      {expanded && (
        <div className={styles.eventDetails} data-testid={`event-details-${event.id}`}>
          <p className={styles.detailsTitle}><IntlMessages id="ui.home.diagnosticsEventPanel.technicalDetails" /></p>
          <div className={styles.detailsGrid}>
            {event.source && <Detail label={<IntlMessages id="ui.home.diagnosticsEventPanel.source" />} value={event.source} />}
            {event.jobId && <Detail label={<IntlMessages id="ui.home.diagnosticsEventPanel.jobId" />} value={event.jobId} />}
            {event.requestId && <Detail label={<IntlMessages id="ui.home.diagnosticsEventPanel.requestId" />} value={event.requestId} />}
            {event.fingerprint && <Detail label={<IntlMessages id="ui.home.diagnosticsEventPanel.fingerprint" />} value={event.fingerprint} />}
            {repeatCount > 1 && <Detail label={<IntlMessages id="ui.home.diagnosticsEventPanel.occurrences" />} value={repeatCount} />}
          </div>
          {event.payload && (
            <details className={styles.payloadDisclosure}>
              <summary><IntlMessages id="ui.home.diagnosticsEventPanel.rawEventPayload" /></summary>
              <pre className={styles.payloadBlock}>{JSON.stringify(event.payload, null, 2)}</pre>
            </details>
          )}
          {event.payloadTruncated && (
            <span className={styles.truncatedNotice}><IntlMessages id="ui.home.diagnosticsEventPanel.payloadWasTruncatedWhenTheEventWasRecorded" /></span>
          )}
        </div>
      )}
    </li>
  );
}

function Detail({ label, value }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

EventRow.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number.isRequired,
    severity: PropTypes.string.isRequired,
    scope: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    occurredAt: PropTypes.string,
    repeatCount: PropTypes.number,
    dedupCount: PropTypes.number,
    payload: PropTypes.object,
    source: PropTypes.string,
    requestId: PropTypes.string,
    jobId: PropTypes.string,
    fingerprint: PropTypes.string,
    payloadTruncated: PropTypes.bool,
  }).isRequired,
};

export default function DiagnosticsEventPanel({
  events,
  hasMore,
  nextSinceId,
  isLoading,
  error,
  onLoadMore,
  onSendToSupport,
}) {
  const t = useLocalizedMessage();
  const [severityFilter, setSeverityFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const scopes = useMemo(
    () => [...new Set(events.map((event) => event.scope).filter(Boolean))].sort(),
    [events],
  );
  const visibleEvents = useMemo(() => events.filter((event) => (
    (severityFilter === 'all' || event.severity === severityFilter)
    && (scopeFilter === 'all' || event.scope === scopeFilter)
  )), [events, severityFilter, scopeFilter]);

  function renderBody() {
    if (isLoading && events.length === 0) {
      return (
        <div className={styles.stateContainer} data-testid="events-loading">
          <CircularProgress size={24} />
          <p><IntlMessages id="ui.home.diagnosticsEventPanel.checkingRecentActivity" /></p>
        </div>
      );
    }
    if (error && events.length === 0) {
      return (
        <div className={`${styles.stateContainer} ${styles.errorState}`} data-testid="events-error">
          <ErrorIcon className={styles.stateIcon} />
          <strong><IntlMessages id="ui.home.diagnosticsEventPanel.recentActivityCouldNotBeLoaded" /></strong>
          <p>{error}</p>
        </div>
      );
    }
    if (!isLoading && events.length === 0) {
      return (
        <div className={styles.stateContainer} data-testid="events-empty">
          <span className={styles.emptyIcon}><CheckCircle /></span>
          <strong><IntlMessages id="ui.home.diagnosticsEventPanel.noRecentIssues" /></strong>
          <p><IntlMessages id="ui.home.diagnosticsEventPanel.nothingRequiringAttentionWasRecordedInTheLast24Hours" /></p>
        </div>
      );
    }
    return (
      <>
        <div className={styles.filters} aria-label={t('operator.diag.events.filterAriaLabel')}>
          <div className={styles.severityFilters}>
            {SEVERITY_FILTERS.map((severity) => {
              const count = severity === 'all'
                ? events.length
                : events.filter((event) => event.severity === severity).length;
              if (severity !== 'all' && count === 0) return null;
              return (
                <button
                  type="button"
                  key={severity}
                  className={severityFilter === severity ? styles.filterActive : styles.filterButton}
                  onClick={() => setSeverityFilter(severity)}
                  data-testid={`events-filter-${severity}`}
                >
                  {humanize(severity, t)}
                  <span>{count}</span>
                </button>
              );
            })}
          </div>
          {scopes.length > 1 && (
            <label className={styles.scopeFilter}>
              <span><IntlMessages id="ui.home.diagnosticsEventPanel.area" /></span>
              <select
                value={scopeFilter}
                onChange={(event) => setScopeFilter(event.target.value)}
                data-testid="events-scope-filter"
              >
                <option value="all"><IntlMessages id="ui.home.diagnosticsEventPanel.allAreas" /></option>
                {scopes.map((scope) => <option key={scope} value={scope}>{humanize(scope, t)}</option>)}
              </select>
            </label>
          )}
        </div>
        {visibleEvents.length > 0 ? (
          <ul className={styles.eventList} data-testid="events-list">
            {visibleEvents.map((event) => <EventRow key={event.id} event={event} />)}
          </ul>
        ) : (
          <div className={styles.filteredEmpty} data-testid="events-filtered-empty">
            <IntlMessages id="ui.home.diagnosticsEventPanel.noEventsMatchTheseFilters" />
          </div>
        )}
        {hasMore && (
          <div className={styles.loadMoreRow}>
            <Button
              size="small"
              variant="outlined"
              disabled={isLoading}
              onClick={() => onLoadMore && onLoadMore(nextSinceId)}
              data-testid="events-load-more"
            >
              {isLoading ? <CircularProgress size={14} /> : t('operator.diag.events.loadEarlier')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <section className={styles.panel} data-testid="diagnostics-event-panel">
      <div className={styles.panelHeader}>
        <div>
          <p className={styles.eyebrow}><IntlMessages id="ui.home.diagnosticsEventPanel.activity" /></p>
          <h3 className={styles.panelTitle}><IntlMessages id="ui.home.diagnosticsEventPanel.recentIssues" /></h3>
          <p className={styles.panelSubtitle}><IntlMessages id="ui.home.diagnosticsEventPanel.operationalEventsThatCanExplainAChangeInSearchBehaviour" /></p>
        </div>
        <div className={styles.panelActions}>
          {onSendToSupport && (
            <Button size="small" variant="outlined" onClick={onSendToSupport} disabled={isLoading} data-testid="events-send-to-support">
              <IntlMessages id="ui.home.diagnosticsEventPanel.createSupportReport" />
            </Button>
          )}
        </div>
      </div>
      {renderBody()}
    </section>
  );
}

DiagnosticsEventPanel.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    severity: PropTypes.string.isRequired,
    scope: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
  })).isRequired,
  hasMore: PropTypes.bool,
  nextSinceId: PropTypes.number,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onLoadMore: PropTypes.func,
  onSendToSupport: PropTypes.func,
};

DiagnosticsEventPanel.defaultProps = {
  hasMore: false,
  nextSinceId: 0,
  isLoading: false,
  error: null,
  onLoadMore: null,
  onSendToSupport: null,
};
